import { styled, SxProp, useBreakpointIndex } from 'dripsy';
import React, { Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute } from '@react-navigation/native';

import {
  Button,
  EmptyResultView,
  Loader,
  LogoView,
  ScrollView,
  Separator,
  SubscriptionPlanItem,
  SubscriptionPlanItemStyleEnum,
  SubscriptionPlansLayout,
  TitleView,
} from '@components';
import {
  AuthNavigatorNavigationPropType,
  AuthNavigatorRoutePropType,
  screens,
} from '@navigation';
import {
  useAsyncInit,
  useNavOptionsSet,
  useSubscriptionPlansLayout,
} from '@hooks';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { parseApiError } from '@utils';
import { ToastService } from '@services';
import { TariffPlanCodeEnum } from '@constants';

const Container = styled(ScrollView)({
  flex: 1,
  bg: '$background0',
});

function contentContainerStyle(props: EdgeInsets): SxProp {
  return (theme) => ({
    flexGrow: 1,
    px: ['$6', '$0'],
    pt: [theme.space.$13 + props.top, '$0'],
    pb: theme.space.$6 + props.bottom,
    alignItems: [null, 'center'],
    justifyContent: [null, 'center'],
  });
}

const LoaderContainer = styled(View)({
  flex: [1, 0],
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: [null, 360],
});

const EmptyViewContainer = styled(LoaderContainer)({});

const ItemSeparator = styled(View)({
  pl: '$15',
});

const ButtonContainer = styled(View)({
  width: [null, 300],
});

const TRIAL_PLAN = {
  style: SubscriptionPlanItemStyleEnum.Choose,
  code: TariffPlanCodeEnum.Trial,
};

const MONTHLY_PLAN = {
  style: SubscriptionPlanItemStyleEnum.Choose,
  code: TariffPlanCodeEnum.Monthly,
};

const QUATERLY_PLAN = {
  style: SubscriptionPlanItemStyleEnum.Choose,
  code: TariffPlanCodeEnum.Quarterly,
};

const YEARLY_PLAN = {
  style: SubscriptionPlanItemStyleEnum.Promote,
  code: TariffPlanCodeEnum.Yearly,
};

const SUBSCRIPTIONS = [
  TRIAL_PLAN,
  MONTHLY_PLAN,
  QUATERLY_PLAN,
  YEARLY_PLAN,
];

type NavigationPropType = AuthNavigatorNavigationPropType<'Referral'>;
type RoutePropType = AuthNavigatorRoutePropType<'Referral'>;

export const ReferralScreen = observer(() => {
  const bIndex = useBreakpointIndex();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const nav = useNavigation<NavigationPropType>();
  const route = useRoute<RoutePropType>();
  const viewer = useViewerModel();
  const subscriptions = useSubscriptionPlansLayout(SUBSCRIPTIONS, {
    withInvite: !!route.params?.invite,
  });

  useNavOptionsSet({ title: t('referral.title') });

  useAsyncInit(async () => {
    try {
      await viewer.tariffPlans.fetchPlans.run({
        referrer: route.params?.invite,
      });
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  function navigateToSignUp() {
    nav.navigate(screens.SignUp, route.params);
  }

  const isError =
    Object.keys(viewer.tariffPlans.byCode).length === 0 &&
    viewer.tariffPlans.fetchPlans.isError;

  return (
    <Container contentContainerSx={contentContainerStyle(insets)}>
      {bIndex > 0 ? (
        <TitleView
          title={t('referral.title')}
          bottomText={t('referral.subtitle')}
        />
      ) : (
        <LogoView
          title={t('referral.title')}
          bottomText={t('referral.subtitle')}
        />
      )}

      <Separator size="$7" />

      {viewer.tariffPlans.fetchPlans.inProgress && (
        <LoaderContainer>
          <Loader size="large" />
        </LoaderContainer>
      )}

      {!viewer.tariffPlans.fetchPlans.inProgress && isError && (
        <EmptyViewContainer>
          <EmptyResultView />
        </EmptyViewContainer>
      )}

      {!viewer.tariffPlans.fetchPlans.inProgress && !isError && (
        <SubscriptionPlansLayout>
          {subscriptions.map((item, itemIndex, array) => (
            <Fragment key={itemIndex}>
              {bIndex > 2 && <ItemSeparator />}
              <SubscriptionPlanItem
                key={itemIndex}
                item={item}
                style={item.style}
              />
              {bIndex > 2 && itemIndex === array.length - 1 && (
                <ItemSeparator />
              )}
            </Fragment>
          ))}
        </SubscriptionPlansLayout>
      )}

      <Separator size={['$6', '$9']} />

      <ButtonContainer>
        <Button onPress={navigateToSignUp}>
          {t('referral.next')}
        </Button>
      </ButtonContainer>
    </Container>
  );
});
